import React, { useEffect, useRef, useState } from "react";

const steps = [
  {
    title: "1. Projektanfrage",
    text: `Ihr Unternehmen tritt mit einer konkreten Fragestellung oder Projektidee an Qlance heran. 
Gemeinsam klären wir erste Eckdaten: Projektziel, Rahmen/ Umfang, Dauer und gewünschte Kompetenzen des Teams.`,
  },
  {
    title: "2. Projektdefinition & Scoping",
    text: `Im gemeinsamen Scoping-Call definieren wir präzise Ziele, Rahmenbedingungen und Abstimmungsintervalle für Ihr Projekt.`,
  },
  {
    title: "3. Matching & Teambildung",
    text: `Qlance stellt ein maßgeschneidertes Projektteam zusammen – bestehend aus ausgewählten Studierenden führender europäischer Business Schools. 
Je nach Projektkomplexität binden wir erfahrene Senior Advisors ein – ehemalige Führungskräfte mit tiefem Branchenwissen –, 
die das Team inhaltlich begleiten und Impulse setzen.`,
  },
  {
    title: "4. Projektstart & Umsetzung",
    text: `Nach einem gemeinsamen Kick-off startet die eigenständige Arbeit des Teams. Die Talente arbeiten strukturiert und eng abgestimmt mit dem Unternehmen an den gesetzten Zielen.  
Ein Qlance-Projektcoach sorgt für methodische Führung und Qualitätssicherung.`,
  },
  {
    title: "5. Ergebnispräsentation",
    text: `Zum Projektabschluss präsentiert das Team die Ergebnisse. 
In einem gemeinsamen Wrap-up werden Erkenntnisse, Umsetzungsmöglichkeiten und potenzielle Folgeprojekte besprochen.`,
  },
];

const InfoSectionTimeline = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  const intervalRef = useRef(null);
  const resumeTimeoutRef = useRef(null);

  // ✅ SWIPE: refs (NEU)
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchDeltaXRef = useRef(0);
  const isSwipingRef = useRef(false);
  const swipeLockRef = useRef(false); // verhindert doppelte Trigger

  const SWIPE_THRESHOLD_PX = 40; // je größer, desto "schwerer" auszulösen

  useEffect(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!isAutoPlay) return;

    intervalRef.current = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % steps.length);
    }, 5000);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAutoPlay]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      if (resumeTimeoutRef.current) window.clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  const handleStepSelect = (index) => {
    setActiveIndex(index);
    setIsAutoPlay(false);

    if (resumeTimeoutRef.current) {
      window.clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }

    resumeTimeoutRef.current = window.setTimeout(() => {
      setIsAutoPlay(true);
    }, 10000);
  };

  // ✅ SWIPE: handlers (NEU)
  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartXRef.current = t.clientX;
    touchStartYRef.current = t.clientY;
    touchDeltaXRef.current = 0;
    isSwipingRef.current = false;
  };

  const handleTouchMove = (e) => {
    if (swipeLockRef.current) return;

    const t = e.touches[0];
    const dx = t.clientX - touchStartXRef.current;
    const dy = t.clientY - touchStartYRef.current;

    // erst entscheiden, ob horizontal swipe oder vertical scroll
    if (!isSwipingRef.current) {
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        isSwipingRef.current = true;
      } else {
        return; // vertikaler scroll -> nichts machen
      }
    }

    touchDeltaXRef.current = dx;

    // nur wenn wirklich horizontal swipen -> scroll blocken
    e.preventDefault();
  };

  const handleTouchEnd = () => {
    if (swipeLockRef.current) return;

    const dx = touchDeltaXRef.current;

    if (!isSwipingRef.current) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;

    // lock gegen doppeltes Triggern (besonders auf manchen Androids)
    swipeLockRef.current = true;
    window.setTimeout(() => {
      swipeLockRef.current = false;
    }, 250);

    if (dx < 0) {
      // swipe left -> next
      handleStepSelect((activeIndex + 1) % steps.length);
    } else {
      // swipe right -> prev
      handleStepSelect((activeIndex - 1 + steps.length) % steps.length);
    }
  };

  return (
    <section className="info-section-timeline" id="timeline">
      <div className="timeline-header">
        <div className="timeline-underline" />
        <h2 className="timeline-title">
          Ablauf eines klassischen <br />
          <span className="timeline-highlight">Beratungsprojektes</span> bei Qlance
        </h2>
      </div>

      <div className="timeline-wrapper">
        <div className="timeline-mobile-active">{steps[activeIndex].title}</div>

        <div className="timeline-bar">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`timeline-dot ${activeIndex === index ? "active" : ""}`}
              style={{ left: `${(index / (steps.length - 1)) * 100}%` }}
              onClick={() => handleStepSelect(index)}
            ></div>
          ))}

          {steps.map((step, index) => (
            <div
              key={`label-${index}`}
              className={`timeline-label ${index % 2 === 0 ? "label-top" : "label-bottom"}`}
              style={{ left: `${(index / (steps.length - 1)) * 100}%` }}
              onClick={() => handleStepSelect(index)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleStepSelect(index);
              }}
            >
              {step.title}
            </div>
          ))}
        </div>

        {/* ✅ NUR HIER: touch events hinzufügen (ansonsten unverändert) */}
        <div
          className="timeline-content"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {steps.map((step, index) => (
            <div
              key={index}
              className={`timeline-step timeline-glass-card ${
                index === activeIndex ? "center" : index < activeIndex ? "left" : "right"
              }`}
            >
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InfoSectionTimeline;
